import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import axios from "axios";
import { createChart } from "lightweight-charts";
import { API, isAbortError, makeAbortable } from "../components/atlas/helpers";
import { Search, Filter, Layers, Database, Code2, RefreshCw } from "lucide-react";
import { useAtlas } from "../store/atlasStore";


function fmt(v, d = 3) {
  if (v == null || !isFinite(+v)) return "—";
  return Number(v).toFixed(d);
}

function toUnix(isoStr) {
  return Math.floor(new Date(isoStr).getTime() / 1000);
}

const PERSPECTIVES = [
  "Magnitude", "Motion", "Geometry", "Relative", "Position",
  "Statistics", "Distribution", "Entropy", "Expansion", "Clustering", "Regime"
];

export function FeaturesWorkspace() {
  const { state } = useAtlas();
  const { provider, symbol, interval } = state;

  const [features, setFeatures] = useState([]);
  const [vocab, setVocab] = useState({ layers: [], perspectives: [] });
  const [selected, setSelected] = useState(null);
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filters
  const [q, setQ] = useState("");
  const [selectedLayer, setSelectedLayer] = useState("");
  const [selectedPersp, setSelectedPersp] = useState("");
  const [selectedModule, setSelectedModule] = useState("");

  // Tabs: "explorer" | "correlation"
  const [activeTab, setActiveTab] = useState("explorer");

  // Correlation state
  const [corrData, setCorrData] = useState(null);
  const [corrLoading, setCorrLoading] = useState(false);
  const [corrPersp, setCorrPersp] = useState("");

  const chartRef = useRef(null);
  const chartInst = useRef(null);
  const seriesRef = useRef(null);

  // Load vocab and initial feature list
  useEffect(() => {
    axios.get(`${API}/registry/vocab`).then(r => setVocab(r.data)).catch(() => {});
    axios.get(`${API}/registry/features`).then(r => {
      setFeatures(r.data);
      if (r.data.length) setSelected(r.data[0]);
    }).catch(() => {});
  }, []);

  const modules = useMemo(() => {
    return [...new Set(features.map(f => f.module))].filter(Boolean).sort();
  }, [features]);

  const filteredFeatures = useMemo(() => {
    return features.filter(f => {
      if (selectedLayer && String(f.layer) !== String(selectedLayer)) return false;
      if (selectedPersp && f.perspective !== selectedPersp) return false;
      if (selectedModule && f.module !== selectedModule) return false;
      if (q) {
        const ql = q.toLowerCase();
        return f.id.toLowerCase().includes(ql) || (f.name || "").toLowerCase().includes(ql);
      }
      return true;
    });
  }, [features, q, selectedLayer, selectedPersp, selectedModule]);

  // Fetch points when selected feature changes OR when instrument context changes
  const fetchPoints = useCallback(async (fid) => {
    if (!fid) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/features/values`, {
        params: { feature_id: fid, provider, symbol, interval, period: "1y" }
      });
      setPoints(r.data.points || []);
    } catch {}
    setLoading(false);
  }, [provider, symbol, interval]);

  useEffect(() => {
    if (selected?.id) {
      fetchPoints(selected.id);
    }
  }, [selected, fetchPoints]);

  // Chart setup
  useEffect(() => {
    if (activeTab !== "explorer" || !chartRef.current) return;

    const chart = createChart(chartRef.current, {
      layout: {
        background: { color: "transparent" },
        textColor: "#8899aa",
        fontSize: 10,
        fontFamily: "'JetBrains Mono', monospace",
      },
      grid: { vertLines: { color: "#161d28" }, horzLines: { color: "#161d28" } },
      rightPriceScale: { borderColor: "#161d28" },
      timeScale: { borderColor: "#161d28", timeVisible: true },
      crosshair: { mode: 1 },
      handleScroll: true,
      handleScale: true,
    });

    const lineSeries = chart.addLineSeries({
      color: "#3b82f6",
      lineWidth: 1.5,
    });

    chartInst.current = chart;
    seriesRef.current = lineSeries;

    const ro = new ResizeObserver(() => {
      if (chartRef.current) {
        chart.applyOptions({
          width: chartRef.current.clientWidth,
          height: chartRef.current.clientHeight,
        });
      }
    });
    ro.observe(chartRef.current);

    return () => {
      ro.disconnect();
      chart.remove();
      chartInst.current = null;
    };
  }, [activeTab]);

  // Feed points data to chart
  useEffect(() => {
    if (activeTab === "explorer" && seriesRef.current && points.length) {
      const data = points
        .filter(p => p.value != null && isFinite(p.value))
        .map(p => ({
          time: toUnix(p.time),
          value: p.value
        })).sort((a, b) => a.time - b.time);
      seriesRef.current.setData(data);
      chartInst.current?.timeScale().fitContent();
    }
  }, [points, activeTab]);

  // Load correlations
  const fetchCorrelations = useCallback(async () => {
    setCorrLoading(true);
    try {
      const r = await axios.get(`${API}/features/correlation`, {
        params: { provider, symbol, interval, period: "1y", perspective: corrPersp || undefined, limit: 28 }
      });
      setCorrData(r.data);
    } catch {}
    setCorrLoading(false);
  }, [provider, symbol, interval, corrPersp]);

  useEffect(() => {
    if (activeTab === "correlation") {
      fetchCorrelations();
    }
  }, [activeTab, fetchCorrelations]);

  const cellColor = (v) => {
    const a = Math.min(1, Math.abs(v));
    return v >= 0 ? `rgba(34,197,94,${a * 0.45})` : `rgba(244,63,94,${a * 0.45})`;
  };

  return (
    <div style={{ display: "grid", gridTemplateColumns: "280px 1fr", height: "100%", gap: 4, padding: 4 }}>
      {/* ── LEFT Column: Feature Explorer List & Filters ─────────────── */}
      <div className="panel" style={{ minHeight: 0, display: "flex", flexDirection: "column" }}>
        <div className="panel-header">
          <span className="panel-title">Feature Library</span>
          <span className="badge">{filteredFeatures.length} / {features.length}</span>
        </div>
        
        {/* Filters */}
        <div style={{ padding: 8, borderBottom: "1px solid var(--border-dim)", display: "flex", flexDirection: "column", gap: 5, flexShrink: 0 }}>
          <div style={{ position: "relative" }}>
            <Search size={11} style={{ position: "absolute", left: 6, top: 7, color: "var(--text-tertiary)" }} />
            <input
              type="text"
              className="inp"
              placeholder="Search features…"
              value={q}
              onChange={e => setQ(e.target.value)}
              style={{ paddingLeft: 20, height: 24 }}
            />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4 }}>
            <select className="inp" value={selectedLayer} onChange={e => setSelectedLayer(e.target.value)} style={{ height: 22 }}>
              <option value="">All Layers</option>
              {vocab.layers && typeof vocab.layers === "object" && !Array.isArray(vocab.layers) ? (
                Object.keys(vocab.layers).map(l => (
                  <option key={l} value={l}>Layer {l} — {vocab.layers[l]}</option>
                ))
              ) : (
                [1, 2, 3, 4, 5].map(l => (
                  <option key={l} value={l}>Layer {l}</option>
                ))
              )}
            </select>
            <select className="inp" value={selectedModule} onChange={e => setSelectedModule(e.target.value)} style={{ height: 22 }}>
              <option value="">All Modules</option>
              {modules.map(m => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>
          <select className="inp" value={selectedPersp} onChange={e => setSelectedPersp(e.target.value)} style={{ height: 22 }}>
            <option value="">All Perspectives</option>
            {PERSPECTIVES.map(p => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </div>

        {/* Feature List */}
        <div style={{ flex: 1, overflowY: "auto", contentVisibility: "auto" }}>
          {filteredFeatures.map(f => (
            <div
              key={f.id}
              style={{
                padding: "6px 8px",
                borderBottom: "1px solid var(--border-dim)",
                cursor: "pointer",
                background: selected?.id === f.id ? "var(--bg-hover)" : "transparent",
              }}
              onClick={() => {
                setSelected(f);
                setPoints([]);
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
                <span className="mono" style={{ fontSize: 10, fontWeight: 600, color: selected?.id === f.id ? "var(--blue)" : "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 190 }} title={f.id}>
                  {f.id?.replace("price.", "")}
                </span>
                <span className="badge" style={{ fontSize: 8, padding: "0 3px" }}>L{f.layer}</span>
              </div>
              <div style={{ display: "flex", gap: 4, fontSize: 9, color: "var(--text-tertiary)" }}>
                <span>{f.module}</span>
                <span>•</span>
                <span>{f.perspective}</span>
              </div>
            </div>
          ))}
          {!filteredFeatures.length && (
            <div style={{ padding: 16, textAlign: "center", color: "var(--text-tertiary)", fontSize: 11 }}>No features match filters</div>
          )}
        </div>
      </div>

      {/* ── RIGHT Column: Feature Profile / Correlation Tabs ─────────── */}
      <div style={{ display: "flex", flexDirection: "column", gap: 4, minHeight: 0 }}>
        {/* Tabs switcher header */}
        <div className="panel" style={{ flexShrink: 0, padding: 4 }}>
          <div style={{ display: "flex", gap: 4 }}>
            {[
              { id: "explorer", label: "Registry Explorer" },
              { id: "correlation", label: "Correlation Matrix" },
            ].map(t => (
              <button
                key={t.id}
                className={`btn${activeTab === t.id ? " btn-primary" : ""}`}
                style={{ padding: "4px 12px", height: 26 }}
                onClick={() => setActiveTab(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── TAB 1: Feature Registry Explorer ────────────────────────── */}
        {activeTab === "explorer" && (
          <div style={{ flex: 1, display: "grid", gridTemplateRows: "1fr 200px", gap: 4, minHeight: 0 }}>
            {/* Chart Area */}
            <div className="panel" style={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
              <div className="panel-header">
                <span className="panel-title">{selected ? `Time Series — ${selected.id}` : "Feature Chart"}</span>
                {loading && <RefreshCw size={11} className="spin text-dim" />}
              </div>
              <div style={{ flex: 1, position: "relative", overflow: "hidden" }}>
                <div ref={chartRef} style={{ position: "absolute", inset: 0 }} />
              </div>
            </div>

            {/* Profile Detail */}
            <div className="panel" style={{ display: "flex", flexDirection: "column" }}>
              <div className="panel-header"><span className="panel-title">Feature Metadata</span></div>
              <div className="panel-body" style={{ overflowY: "auto", padding: "8px 10px" }}>
                {selected ? (
                  <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 12 }}>
                    <div>
                      <div className="kv-row" style={{ borderBottom: "none", padding: "2px 0" }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary)" }}>{selected.name || selected.id}</span>
                      </div>
                      <div style={{ fontSize: 11, color: "var(--text-secondary)", marginTop: 4, lineHeight: 1.4 }}>
                        {selected.description || "No description provided."}
                      </div>
                      {selected.formula && (
                        <div style={{ marginTop: 8 }}>
                          <div className="label" style={{ marginBottom: 2 }}>Mathematical Formula</div>
                          <div className="mono" style={{ background: "var(--bg-raised)", border: "1px solid var(--border-dim)", borderRadius: 3, padding: "5px 8px", fontSize: 10, color: "var(--blue)" }}>
                            {selected.formula}
                          </div>
                        </div>
                      )}
                    </div>
                    <div>
                      {[
                        ["Feature ID", selected.id],
                        ["Layer", `L${selected.layer}`],
                        ["Module", selected.module],
                        ["Perspective", selected.perspective],
                        ["Family", selected.family || "—"],
                        ["Warmup Bars", selected.warmup_bars ?? 0],
                      ].map(([k, v]) => (
                        <div className="kv-row" key={k}>
                          <span className="k">{k}</span>
                          <span className="v mono" style={{ fontSize: 10 }}>{v}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div style={{ color: "var(--text-tertiary)", fontSize: 11 }}>Select a feature to see profile</div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── TAB 2: Correlation Matrix ────────────────────────────────── */}
        {activeTab === "correlation" && (
          <div className="panel" style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            <div className="panel-header" style={{ height: "auto", padding: "6px 8px", gap: 8 }}>
              <span className="panel-title">Correlation Matrix</span>
              <select className="inp" value={corrPersp} onChange={e => setCorrPersp(e.target.value)} style={{ width: 140, height: 22 }}>
                <option value="">All Perspectives</option>
                {PERSPECTIVES.map(p => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
              <button className="btn btn-primary" onClick={fetchCorrelations} disabled={corrLoading} style={{ height: 22, padding: "2px 8px" }}>
                {corrLoading ? <RefreshCw size={11} className="spin" /> : "Recompute"}
              </button>
            </div>
            <div className="panel-body" style={{ overflow: "auto", background: "var(--bg-base)" }}>
              {corrLoading && !corrData && (
                <div style={{ padding: 16, color: "var(--text-tertiary)", fontSize: 11 }}>Computing matrix…</div>
              )}
              {corrData?.matrix?.length ? (
                <table className="mono" style={{ borderCollapse: "collapse", fontSize: 9 }}>
                  <thead>
                    <tr>
                      <th />
                      {corrData.features.map((f, i) => (
                        <th
                          key={i}
                          className="text-dim"
                          style={{
                            padding: "2px 4px",
                            writingMode: "vertical-rl",
                            transform: "rotate(180deg)",
                            fontSize: 9,
                            textAlign: "left",
                            whiteSpace: "nowrap"
                          }}
                        >
                          {f.replace("price.", "").slice(0, 24)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {corrData.matrix.map((row, i) => (
                      <tr key={i} style={{ borderBottom: "1px solid var(--border-dim)" }}>
                        <td
                          className="text-dim pr-2"
                          style={{
                            padding: "2px 6px",
                            fontSize: 9,
                            whiteSpace: "nowrap"
                          }}
                        >
                          {corrData.features[i].replace("price.", "").slice(0, 24)}
                        </td>
                        {row.map((v, j) => (
                          <td
                            key={j}
                            title={`${corrData.features[i]} × ${corrData.features[j]}: ${fmt(v, 3)}`}
                            style={{
                              width: 22,
                              height: 22,
                              background: cellColor(v || 0),
                              textAlign: "center",
                              color: Math.abs(v || 0) > 0.6 ? "#ffffff" : "var(--text-secondary)",
                              fontSize: 9,
                              fontWeight: Math.abs(v || 0) > 0.5 ? 600 : 400,
                              borderRight: "1px solid var(--border-dim)"
                            }}
                          >
                            {v == null ? "" : v.toFixed(1).replace("0.", ".")}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div style={{ padding: 16, color: "var(--text-tertiary)", fontSize: 11 }}>No correlation data computed yet</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

